import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from "react-router-dom";
import cx from 'classnames';
import _ from 'lodash';
import dateformat from "dateformat";
import { Button } from "react-bootstrap";
import { createMixpanelEvent } from "actions/mixpanel";
import { MIXPANEL_REPORTS_DOWNLOADED } from "constants/mixpanelEvents";
import { getStoryboardMixpanelEventPayload } from 'services/mixpanelUtil';
import { fetchAssetAnalyticsForDownload, invalidateAssetDownloadData, fetchAnalyticsChartForAssetAnalytics, fetchStoryboardAssetAnalytics } from 'actions/storyboardReports';
import { EmptyMessage, AssetAnalyticsHeader, LoadingMessage, TableInfo, getWidthForAnalyticsBar } from '.';
import { TrendBarChart, TrendsAreaChart } from './charts';
import { TableFooter } from './tableFooter';
import StoryboardContext from 'components/Storyboard/context';
import { convertDatesToTimezone } from 'services/report.service';
import { padNumber } from 'services/util';
import FileService from 'services/file.service';

import { CSVLink } from 'react-csv';
import { IKImage } from 'imagekitio-react';
import LinesEllipsis from 'react-lines-ellipsis';
import Tooltip from "components/Tooltip";

import styles from '../report.module.scss';

const defaultContentImage = `/cleverstory/images/default-content.png`;

export function AssetAnalytics(props) {
  const dispatch = useDispatch();

  let { storyboardId } = useParams();
  const assets = useSelector(state => _.get(state, 'storyboards.storyboard.reports.assetAnalytics.items'));
  const pagination = useSelector(state => _.get(state, 'storyboards.storyboard.reports.assetAnalytics.pagination'));
  const loading = useSelector(state => _.get(state, 'storyboards.storyboard.reports.assetAnalytics.loading'));
  const [currentChart, setCurrentChart] = useState("table_view");

  useEffect(() => {
    if (props.date) {
      handlePageChange(0);
    }
  }, [props.date]);

  const handlePageChange = (page) => {
    handleFetchAssetAnalyticsReport(page);
  }

  const handleFetchAssetAnalyticsReport = (page) => {
    var request = {
      storyboard: storyboardId,
      ...convertDatesToTimezone(props.date),
    }

    dispatch(fetchStoryboardAssetAnalytics(request, page, 5));
  }

  const handleChartChange = (value) => {
    setCurrentChart(value)
  }

  return (
    <div className={styles.assetAnalyticsWrapper}>
      <div className={styles.assetAnalyticsContainer}>
        <div className={styles.assetAnalyticsHeading}>
          <TableInfo
            name="Asset Analytics"
            pagination={pagination}
          />
          <ExportCSV date={props.date} />
        </div>

        {currentChart == "table_view" &&
          <AssetAnalyticsHeader />
        }

        {/* {currentChart == "pie_chart_view" &&
          <DropdownFilter arrangeOption={viewOption} handleArrange={handleArrange} />
        } */}

        {currentChart == "table_view" &&
          !loading && assets && assets.map((data, index) => {
            return (
              <AssetsReportView
                key={index}
                data={data}
                loading={loading}
                storyboard={storyboardId}
                index={index}
                date={props.date}
              />
            )
          })
        }

        {loading && (assets && assets.length > 0) &&
          <LoadingMessage />
        }

        {(assets && assets.length == 0) && !loading &&
          <EmptyMessage />
        }
      </div>

      <TableFooter
        currentChart={currentChart}
        disableView={['graph_view', 'pie_chart_view', 'table_view']}
        handleChartChange={handleChartChange}
        handlePageChange={handlePageChange}
        pagination={pagination}
        paginationName={"Assets"}
      />
    </div>
  )
}

function ExportCSV(props) {
  const storyboardContext = React.useContext(StoryboardContext);

  const downloads = useSelector(state => _.get(state, 'storyboards.storyboard.assetReports.items'));
  var domain = useSelector(state => _.get(state, 'workspaces.workspace.item.defaultDomain.url') || "");
  const completed = useSelector(state => _.get(state, 'storyboards.storyboard.assetReports.completed'));
  const progress = useSelector(state => _.get(state, 'storyboards.storyboard.assetReports.progress'));
  const [csvData, setCsvData] = useState([]);
  const csvLink = useRef();
  const storyboard = useSelector((state) => _.get(state, "storyboards.storyboard.item", null));

  const headers = [
    { label: "Asset Title", key: "assetTitle" },
    { label: "Content-Type", key: "contentType" },
    { label: "Link to Asset", key: "assetLink" },
    { label: "Added Date", key: "addedDate" },
    { label: "Updated date", key: "updatedDate" },
    { label: "Views", key: "views" },
    { label: "Visitors", key: "visitors" },
    { label: "Avg. Time Spent", key: "avgTime" },
    { label: "Bounce Rate", key: "bounceRate" },
    { label: "Downloads", key: "downloads" },
    { label: "Custom Field", key: "customField" },
    { label: "Tags", key: 'tags' }
  ];


  const dispatch = useDispatch();
  let { storyboardId } = useParams();

  useEffect(() => {
    if (completed) {
      var columns = [];
      downloads && downloads.map((data, index) => {

        var content = _.get(data, 'asset.content');

        const renderCustomFields = (cf) => {
          return `[${cf.map(item => {
            return `${item.name}: {${item.value.map(v => v.value).join(', ')}}`;
          }).join(' | ')}]`;
        };

        const _renderTimeSpent = (seconds) => {
          let minutes = padNumber(Math.floor(seconds / 60), 2);
          seconds = padNumber(Math.floor(seconds % 60), 2);
          var hours = padNumber(Math.floor(minutes / 60), 2);
          minutes = padNumber(minutes % 60, 2);

          hours = hours > 0 ? `${hours}:` : ''
          return `${hours}${minutes}:${seconds}`;
        }

        var cf = content.customFields
        let resultCf = "";
        if (cf !== null && _.size(cf) !== 0) {
          resultCf = renderCustomFields(cf);
        }

        const assetUrl = `${domain}${data.asset.url}`;

        columns[index] = {
          assetTitle: content.asset.name,
          contentType: data.asset.type,
          assetLink: assetUrl,
          addedDate: content.createdDate == undefined ? "" : dateformat(content.createdDate, 'dd mmm yyyy'),
          updatedDate: content.modifiedDate == undefined ? "" : dateformat(content.modifiedDate, 'dd mmm yyyy'),
          views: data.views,
          visitors: data.visitors,
          avgTime: data.timeSpent == 0 ? "00:00" : _renderTimeSpent(data.timeSpent / data.views),
          bounceRate: `${(data.bounceRate * 100 / data.views).toFixed(0)}%`,
          downloads: data.downloads,
          customField: resultCf,
          tags: content.tags
        }
      });

      setCsvData(columns);
    }
  }, [completed]);

  useEffect(() => {
    if (!completed && csvData && csvData.length == 0) {
      return;
    }

    csvLink.current.link.click();

    return () => {
      dispatch(invalidateAssetDownloadData());
    }
  }, [csvData]);

  const fetchAssetsData = () => {
    var mixpanelPayload = getStoryboardMixpanelEventPayload(storyboard.id, storyboard.name, storyboard.type);
    mixpanelPayload = {
      ...mixpanelPayload,
      report_type: 'Asset',
    }

    dispatch(createMixpanelEvent(MIXPANEL_REPORTS_DOWNLOADED, mixpanelPayload));
    dispatch(invalidateAssetDownloadData());

    var request = {
      storyboard: storyboardId,
      ...convertDatesToTimezone(props.date),
    }

    dispatch(fetchAssetAnalyticsForDownload(request, 0, 5))
  }

  return (
    <div>
      <Button variant="transparent" disabled={progress} onClick={() => fetchAssetsData()} className={styles.download}>
        <i className={cx("cleverstory-download", styles.downloadIcon)} />
        <span >{progress ? "Downloading..." : "Download "}</span>
      </Button>

      <CSVLink
        ref={csvLink}
        headers={headers}
        data={csvData}
        filename='Reports.csv'
        className='hidden'
      />
    </div>
  )
}

function AssetsReportView({ loading, data, date }) {
  const dispatch = useDispatch();

  const assetMappingId = _.get(data, 'asset.content.id', null);
  const assetsAnalyticsData = useSelector(state => _.get(state, `storyboards.storyboard.reports.analytics.asset.items[${assetMappingId}]`));

  useEffect(() => {
    const assetData = _.get(data, 'asset.content.asset', null);

    if (assetMappingId){
      var request = {
        "assetMappingId": assetMappingId,
        ...convertDatesToTimezone(date)
      }
      
      dispatch(fetchAnalyticsChartForAssetAnalytics(request, assetData));
    }
  }, [data.asset]);

  if (!data || data.length <= 1) {
    return false;
  }

  const assetData = _.get(data, 'asset');
  const name = getFieldSelector(assetData, "name", '');
  const assetType = getFieldSelector(assetData, "contentType");
  const assetSize = getFieldSelector(assetData, "fileSize");
  
  const icon = getFieldSelector(assetData, "icon");
  const iconUrl = _.get(icon, 'url') || defaultContentImage;

  const _renderTimeSpent = (seconds) => {
    let minutes = padNumber(Math.floor(seconds / 60), 2);
    seconds = padNumber(Math.floor(seconds % 60), 2);
    var hours = padNumber(Math.floor(minutes / 60), 2);
    minutes = padNumber(minutes % 60, 2);

    hours = hours > 0 ? `${hours}:` : ''
    return `${hours}${minutes}:${seconds}`;
  }

  const renderTrends = (analytics, assetData) => {
    const metaData = _.get(assetData, 'asset.content.asset.metadata', null);
    let contentType = FileService.fileContentType(metaData);
    
    switch (contentType) {
      case "DOCUMENT":
          return <TrendBarChart data={analytics} dataKey="totalTime" width={getWidthForAnalyticsBar(analytics)} renderTimeSpent={_renderTimeSpent}/>
      
      case "AUDIO": 
      case "VIDEO":
        contentType = contentType.toLowerCase();
        const totalDuration = _.get(metaData, `${contentType}_duration`, 0);
        const segmentLength = _.get(metaData, `${contentType}_segment_length`, 1);
        const totalSegments = Math.round(totalDuration) / segmentLength;

        const generateAnalytics = (analytics, totalSegments, segmentLength) => {
          let time = 0;

          return Array.from({ length: totalSegments }, (_, index) => {
            const existingItem = analytics.find(item => item.index === index);
            time = time + segmentLength;
            return existingItem ? { ...existingItem, currentTime: time} : { index: index + 1, currentTime: time, totalTime: 0, count: 0 };
          })
        }

        const updatedAnaltyics = generateAnalytics( analytics, totalSegments, segmentLength);

        if(!totalSegments){
          return <span className={styles.hypen}> - </span>
        }
        
        return <TrendsAreaChart data={updatedAnaltyics} height={50} width={getWidthForAnalyticsBar(updatedAnaltyics)} renderTimeSpent={_renderTimeSpent}/>

      default:
          return '-';
      }
  }

  return (
    <div className={styles.assetAnalyticsRow}>
      <div className={cx(styles.wrapper, styles.assetColumn)}>
        <IKImage
          className={styles.assetIcon}
          path={iconUrl}
          transformation={[{
            "width": "600"
          }]}
        />
        <div className={styles.assetDetails}>
          <Tooltip placement="top" content={name}>
            <div className={styles.assetName}>
              <LinesEllipsis lines={2} text={name || ''} />
            </div>
          </Tooltip>
          <div className={styles.assetFormat}>
            <span className={styles.type}>{assetType}</span>
            <span className={styles.size}>{assetSize}</span>
          </div>
        </div>
      </div>

      <div className={cx(styles.wrapper, styles.viewsColumn)}>
        <div className={styles.headline}>{data.views}</div>
      </div>

      <div className={cx(styles.wrapper, styles.visitorsColumn)}>
        <div className={styles.headline}>{data.visitors}</div>
      </div>

      <div className={cx(styles.wrapper, styles.avgTimeSpentColumn)}>
        <div className={styles.headline}>{data.timeSpent == 0 ? "00:00" : _renderTimeSpent(data.timeSpent / data.views)}</div>
      </div>

      <div className={cx(styles.wrapper, styles.bounceColumn)}>
        <div className={styles.headline}>{(data.bounceRate * 100 / data.views).toFixed(0)}%</div>
      </div>

      <div className={cx(styles.wrapper, styles.downloadColumn)}>
        <div className={styles.headline}>{data.downloads}</div>
      </div>

      <div className={cx(styles.wrapper, styles.pageAnalyticsView)}>
        {(assetsAnalyticsData && assetsAnalyticsData.length > 0) ?
          renderTrends(assetsAnalyticsData, data) : <span className={styles.hypen}> - </span>
        }
      </div>
    </div>
  )
}

const getFieldSelector = (mapping, key) => {
  const fields = _.get(mapping, 'fields') || [];
  const field = fields.find(item => item.name === key);

  return _.get(field, 'content', "");
}
